import React, { useEffect, useRef, useState } from 'react';

const GAP = 8;         // gap between trigger and popover
const EDGE = 16;       // minimum clearance from the viewport edges
const MIN_BELOW = 420; // less room than this below the trigger? open above

/* Anchored popover geometry (desktop only): directly beneath the trigger,
   clamped to the viewport, flipped above only when below is too tight. */
function computePos(anchor) {
  const rect = anchor.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const width = Math.min(720, vw - EDGE * 2);
  const left = Math.max(EDGE, Math.min(rect.left, vw - width - EDGE));
  const below = vh - rect.bottom - GAP;
  if (below < MIN_BELOW && rect.top > vh - rect.bottom) {
    return { left, bottom: vh - rect.top + GAP, maxHeight: rect.top - GAP - EDGE };
  }
  return { left, top: rect.bottom + GAP, maxHeight: below - EDGE };
}

export default function CalendarModal({ open, onClose, anchorRef, children }) {
  const overlayRef = useRef(null);
  const cardRef = useRef(null);
  const lastFocused = useRef(null);
  const [pos, setPos] = useState(null);

  // Track the anchored position while open; null = centred overlay (mobile,
  // or no anchor supplied).
  useEffect(() => {
    if (!open) return;
    const anchor = anchorRef?.current;
    if (!anchor) return;
    const mq = window.matchMedia('(max-width: 640px)');
    const update = () => setPos(mq.matches ? null : computePos(anchor));
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    mq.addEventListener('change', update);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      mq.removeEventListener('change', update);
      setPos(null);
    };
  }, [open, anchorRef]);

  useEffect(() => {
    if (!open) return;
    lastFocused.current = document.activeElement;
    document.body.style.overflow = 'hidden';
    const firstFocusable = cardRef.current?.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    firstFocusable?.focus();

    function onKeyDown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'Tab') {
        const focusables = Array.from(
          cardRef.current?.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])') || []
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
      lastFocused.current?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  function handleOverlayClick(e) {
    if (e.target === overlayRef.current) {
      onClose();
    }
  }

  const anchored = !!pos;
  const wrapperStyle = anchored
    ? {
        position: 'fixed',
        margin: 0,
        left: pos.left,
        top: pos.top,
        bottom: pos.bottom,
        maxHeight: Math.max(240, pos.maxHeight),
      }
    : undefined;

  return (
    <div
      className={'shs-calendar-overlay' + (anchored ? ' shs-calendar-overlay-anchored' : '')}
      ref={overlayRef}
      onClick={handleOverlayClick}
      role="presentation"
    >
      <div className="shs-calendar-wrapper" ref={cardRef} style={wrapperStyle}>
        {children}
      </div>
    </div>
  );
}
